import React, { useState } from 'react';
import {
  CardHeader,
  Divider,
  Grid,
  Button,
  Box,
  Card,
  Breadcrumbs,
  Typography,
  Link,
} from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';
import CloseIcon from '@material-ui/icons/Close';
import AddCircleOutlineOutlinedIcon from '@material-ui/icons/AddCircleOutlineOutlined';
import FilterListIcon from '@material-ui/icons/FilterList';
import {
  VerticalBoxContainer,
  PageContentWrapper,
  QuickSearch,
  FilterChip,
  useAPI,
  DeleteModal,
} from '@shared/components';
import PageHeader from '../../components/PageHeader';
import axios from '../../utils/axios';
import useTheme from '../../hooks/useTheme';
import ExcelExportButton from '../../components/ExcelExportButton';
import useGlobalAlertContext from '../../hooks/useGlobalAlertContext';
import { handleChipDelete } from '../../utils/filterHelpers';
import DocumentTypesTable from './Table';
import Filter from './Filter';
import DocumentTypeModal from './Modal';

const fields = [
  { label: 'ID', value: 'id' },
  { label: 'Šifra', value: 'code' },
  { label: 'Naziv', value: 'name' },
  { label: 'Default opcija snimanja', value: 'documentSaveId' },
];

const getLabelText = (field) => {
  const map = { id: 'ID', code: 'Šifra', name: 'Naziv', documentSaveId: 'Default opcija snimanja' };
  return map[field];
};

const getChipValue = (chip, filters) => {
  return filters[chip];
};

const DocumentTypes = () => {
  const theme = useTheme();
  const [sorters, setSorters] = useState([]);
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [triggerRerender, setRerender] = useState(false);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [documentTypeModalVisible, setDocumentTypeModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState();

  const { sendSuccessAlertData, sendErrorAlertData } = useGlobalAlertContext();

  const [documentTypes = { nodes: [], totalCount: 0 }, error, loading] = useAPI(
    () =>
      axios
        .get(`document-types`, {
          params: {
            limit: rowsPerPage,
            offset: page * rowsPerPage,
            conditions: filters,
            orderBy: [...sorters, "'id' desc"],
          },
        })
        .then(({ data }) => data),
    [page, rowsPerPage, filters, sorters, triggerRerender],
  );

  const handleFilterChange = (filterValues) => {
    setPage(0);
    setFilters({ ...filterValues });
  };

  return (
    <VerticalBoxContainer>
      <Filter
        currentValues={filters}
        visible={filterModalVisible}
        setVisible={setFilterModalVisible}
        handleFilterChange={handleFilterChange}
        getLabelText={getLabelText}
        getChipValue={getChipValue}
        setFilters={setFilters}
        setPage={setPage}
      />
      <DocumentTypeModal
        documentType={selectedUnit}
        setDocumentType={setSelectedUnit}
        resetDocumentType={() => setSelectedUnit(undefined)}
        visible={documentTypeModalVisible}
        setVisible={setDocumentTypeModalVisible}
        setRerender={setRerender}
        sendSuccessAlertData={sendSuccessAlertData}
        sendErrorAlertData={sendErrorAlertData}
      />
      <DeleteModal
        objectId={selectedUnit?.id}
        title={`Ukloni tip dokumenta ${
          selectedUnit?.name ? `sa šifrom ${selectedUnit?.code}` : ''
        }`}
        path="document-types"
        message="Uspješno izbrisan tip dokumenta"
        buttonText="Ukloni tip dokumenta"
        promptText={`Da li ste sigurni da želite tip dokumenta ${
          selectedUnit?.name ? `sa šifrom ${selectedUnit?.code}` : ''
        }?`}
        resetObject={() => setSelectedUnit(undefined)}
        visible={deleteModalVisible}
        setVisible={setDeleteModalVisible}
        sendSuccessAlertData={sendSuccessAlertData}
        sendErrorAlertData={sendErrorAlertData}
        setRerender={setRerender}
        setPage={setPage}
        page={page}
        dataLength={documentTypes.nodes.length}
        axios={axios}
        theme={theme}
      />
      <PageHeader
        title="Tipovi dokumenata"
        extra={
          <Grid container spacing={4}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddCircleOutlineOutlinedIcon />}
                onClick={() => setDocumentTypeModalVisible(true)}
              >
                <Box fontSize="small">Novi tip dokumenta</Box>
              </Button>
            </Grid>
          </Grid>
        }
      />
      <PageContentWrapper>
        <Breadcrumbs style={{ paddingBottom: `${theme.spacing(5)}px` }}>
          <Link variant="body1" color="inherit" to="/" component={RouterLink}>
            Početna
          </Link>
          <Typography variant="body1" color="textPrimary">
            Šifarnik: Tipovi dokumenata
          </Typography>
        </Breadcrumbs>
        <Card>
          <CardHeader
            title={
              <>
                <QuickSearch
                  name="name"
                  placeholder="Brza pretraga po nazivu"
                  setFilters={setFilters}
                  mainFilter={filters}
                  setPage={setPage}
                />
              </>
            }
            action={
              <Grid container spacing={4}>
                <Grid item>
                  <Button
                    color="primary"
                    disabled={Object.keys(filters).filter((k) => filters[k])?.length === 0}
                    startIcon={<CloseIcon fontSize="small" />}
                    onClick={() => setFilters({})}
                  >
                    <Box fontSize="small">Ukloni sve filtere</Box>
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<FilterListIcon fontSize="small" />}
                    onClick={() => setFilterModalVisible(true)}
                  >
                    <Box fontSize="small">Filteri</Box>
                  </Button>
                </Grid>
                <Grid item>
                  <ExcelExportButton
                    fields={fields}
                    sorters={[...sorters, "'id' desc"]}
                    filters={{}}
                    routeName="document-types"
                    fileName="sifarnik-tipovi-dokumenata"
                    totalCount={documentTypes.totalCount}
                  />
                </Grid>
              </Grid>
            }
          />
          <Divider />
          {Object.keys(filters).filter((k) => filters[k])?.length !== 0 && (
            <>
              <Box ml={2} p={2} display="flex" alignItems="center" flexWrap="wrap">
                {Object.keys(filters).map(
                  (chip) =>
                    filters[chip] !== undefined && (
                      <FilterChip
                        color="primary"
                        variant="outlined"
                        key={chip}
                        label={`${getLabelText(chip)}: ${getChipValue(chip, filters)}`}
                        onDelete={() => handleChipDelete(chip, setFilters, setPage)}
                      />
                    ),
                )}
              </Box>
            </>
          )}
          <DocumentTypesTable
            loading={loading}
            error={error}
            documentTypes={documentTypes.nodes}
            page={page}
            setPage={setPage}
            sorters={sorters}
            setSorters={setSorters}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            totalCount={documentTypes.totalCount}
            setSelectedUnit={setSelectedUnit}
            setDeleteModalVisible={setDeleteModalVisible}
            setDocumentTypeModalVisible={setDocumentTypeModalVisible}
          />
        </Card>
      </PageContentWrapper>
    </VerticalBoxContainer>
  );
};

export default DocumentTypes;
